import { Cross, ShieldCheck, Hammer, HeartHandshake, BadgeCheck } from "lucide-react";
import { PageHero, SectionHeading, Reveal, CTABand, usePageMeta } from "@/components/kit";
import { HERO_BG, SLOGAN } from "@/lib/site";

export default function AboutPage() {
  usePageMeta(
    "About Us | Redeemer Solutions LLC — Christiana, TN",
    "Redeemer Solutions LLC is a faith-based security technology and low-voltage service company in Christiana, TN. Learn our story, values, and commitment to honest work."
  );
  return (
    <div data-testid="about-page">
      <PageHero
        testid="about-hero"
        eyebrow="About Us"
        lines={["A Company Built", { t: "on Conviction", gold: true }]}
        sub="Redeemer Solutions LLC was founded on a simple belief: keeping people protected and places connected is sacred work, and it deserves to be done with excellence."
        image={HERO_BG}
        imageAlt="Security technology glowing against a dark background"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <SectionHeading
            eyebrow="Our Story"
            title="There's always a solution — it's more than a slogan."
            testid="about-story-heading"
          />
          <Reveal className="space-y-5 mt-8 text-zinc-300 text-base leading-relaxed">
            <p>
              Redeemer Solutions LLC began with a toolbox, a faith, and a frustration with an industry that too often
              over-promises and under-delivers. Based in Christiana, Tennessee, we're a service-only company: we keep
              existing security and low-voltage systems running for churches, schools, businesses, and homes across
              Middle Tennessee.
            </p>
            <p>
              Our name is our mission statement. To redeem means to restore — and that's what we do on every call:
              restore a dark camera, restore a down fiber line before Sunday services, restore a tangled network closet
              into something orderly and dependable.
            </p>
            <p>
              As an ISNetworld Member Contractor, we hold ourselves to verified safety and quality standards. And because
              emergencies don't keep business hours, neither do we — after-hours emergency dispatch is part of who we are,
              not an add-on.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <blockquote className="mt-10 border-l-2 border-gold/50 pl-6">
              <p className="font-display text-lg sm:text-xl text-gold-champagne/90 italic leading-relaxed">
                "Whatever you do, work at it with all your heart, as working for the Lord and not for men."
              </p>
              <cite className="font-mono-label text-[11px] uppercase tracking-[0.24em] text-zinc-500 mt-3 block not-italic">Colossians 3:23</cite>
            </blockquote>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="bg-[#121216] border border-gold/15 rounded-sm p-8 sm:p-10 sticky top-28">
              <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-gold">What Guides Us</p>
              <ul className="mt-8 space-y-7">
                {[
                  { icon: Cross, t: "Faith First", d: "Christian values shape how we quote, schedule, communicate, and stand behind our work." },
                  { icon: ShieldCheck, t: "Integrity in Every Estimate", d: "Honest diagnostics. If a simple repair will do, that's what we'll recommend." },
                  { icon: Hammer, t: "Craftsmanship", d: "Clean cable dressing, labeled panels, tested repairs — details others skip, we sign." },
                  { icon: HeartHandshake, t: "Service to Community", d: "Preferred rates for churches, schools, and emergency personnel facilities." },
                ].map((v) => (
                  <li key={v.t} className="flex gap-4">
                    <v.icon size={20} className="text-gold mt-1 shrink-0" />
                    <div>
                      <h3 className="font-display text-base font-semibold text-[#F4EFE6]">{v.t}</h3>
                      <p className="text-zinc-400 text-sm leading-relaxed mt-1.5">{v.d}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div data-testid="about-isnetworld-badge" className="mt-9 pt-7 border-t border-gold/15 flex items-center gap-3">
                <BadgeCheck size={18} className="text-gold shrink-0" />
                <span className="text-sm text-zinc-300">ISNetworld Member Contractor — safety & quality verified</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="mission-band" className="border-t border-gold/15 bg-[#0D0D10] relative overflow-hidden">
        <div className="absolute inset-0 spotlight pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-20 sm:py-24 text-center">
          <Reveal>
            <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-gold">Our Mission</p>
            <blockquote className="mt-7">
              <p className="font-display text-xl sm:text-2xl lg:text-3xl text-gold-champagne/95 leading-relaxed">
                "Redeemer Solutions was founded to bring balance back to the field."
              </p>
              <p className="text-zinc-300 text-base leading-relaxed mt-6">
                We serve customers who value honesty over hype, offering dependable low-voltage solutions without
                inflated costs, hidden agendas, or unnecessary complexity — because good work should protect the
                client, not exploit them.
              </p>
            </blockquote>
          </Reveal>
        </div>
      </section>

      <CTABand title={SLOGAN} sub="Meet the team that treats your facility like its own. Fast diagnostics, honest quotes, no pressure." />
    </div>
  );
}
